import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import ThemeIcons from '../../theme/Icons';
import ThemeContext from '../../theme/ThemeContext';

import Button from './Button';
import Icon from './Icon';

const StyledButtonWrapper = styled.div`
  text-align: center;
`;

const Zone = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  font-size: 12px;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px dashed ${(props) => props.theme.borderColor};
  background-color: ${(props) => props.theme.secondaryColor};
  color: ${(props) => props.theme.fontColorPrimary};
  outline: none;
  transition: all .15s ease-in-out;

  svg {
    color: ${(props) => props.theme.fontColorPrimary};
    font-size: 16px;
  }

  &:hover {
    cursor: pointer;
    color: ${(props) => props.theme.primaryColor};
    border: 1px dashed ${(props) => props.theme.primaryColor} !important;
    svg {
      color: ${(props) => props.theme.primaryColor};
      font-size: 16px;
    }
  }

`;

const ImageWrapper = styled.div`
  max-width: 100%;
`;

const StyledImg = styled.img`
  width:100%;
  height:100%;
  max-width:350px;
  max-height:350px;
  margin-left: auto;
  margin-right: auto;
`;

const StyledMsg = styled.div`
  margin-bottom: 6px;
`;

const ZoneChild = ({ getInputProps, msgNode, uploadingIndicator, showFilePreview, defaultValue, base64PreviewImageUrl }) => (
  <>
    <input data-testid="dropzone-input" {...getInputProps()} />
    {uploadingIndicator}
    {showFilePreview && (defaultValue || base64PreviewImageUrl || uploadingIndicator) ? null : <StyledMsg>{msgNode}</StyledMsg>}
    {showFilePreview && (defaultValue || base64PreviewImageUrl || uploadingIndicator) ? null : (
      <StyledButtonWrapper>
        <Icon icon={ThemeIcons.settings.upload} />
      </StyledButtonWrapper>
    )}
    {showFilePreview && (defaultValue || base64PreviewImageUrl) ? getImagePreview(defaultValue, base64PreviewImageUrl) : null}
  </>
);

const getImagePreview = (defaultValue, base64PreviewImageUrl) => {

  return (
    <ImageWrapper data-testid="dropzone-image-preview-wrapper">
      <StyledImg
        data-testid="dropzone-image-preview"
        src={base64PreviewImageUrl || defaultValue}
        alt="dropzone-preview"
      />
      <StyledButtonWrapper>
        <Button text="Change" />
      </StyledButtonWrapper>
    </ImageWrapper>
  );

};

const Dropzone = ({
  onDrop,
  uploadingIndicator,
  accept,
  dragMessage,
  multiple = false,
  showFilePreview,
  defaultValue,
  base64PreviewImageUrl,
  styles
}) => {

  const theme = useContext(ThemeContext);

  const dropzoneState = useDropzone({
    accept,
    onDrop,
    multiple,
  });

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = dropzoneState;

  const dragMsg = dragMessage || getMessage(dropzoneState);
  const defaultStyles = { ...styles, borderColor: getColor(dropzoneState, theme) };

  return (
    <Zone
      theme={theme}
      data-testid="dropzone"
      style={defaultStyles}
      {...getRootProps({ isDragActive, isDragAccept, isDragReject })}
    >
      <ZoneChild
        uploadingIndicator={uploadingIndicator}
        msgNode={dragMsg}
        getInputProps={getInputProps}
        showFilePreview={showFilePreview}
        defaultValue={defaultValue}
        base64PreviewImageUrl={base64PreviewImageUrl}
      />
    </Zone>
  );
};

const getColor = ({ isDragActive, isDragAccept, isDragReject }, theme) => {

  if (isDragAccept || isDragActive) {
    return theme.primaryColor;
  } else if (isDragReject) {
    return theme.dangerColor;
  } else {
    return theme.borderColor;
  }

};

const getMessage = ({ isDragActive, isDragAccept, isDragReject }) => {

  if (isDragAccept) {
    return 'Drop your file...';
  } else if (isDragReject) {
    return 'Incorrect file upload';
  } else if (isDragActive) {
    return 'Uploading your file...';
  } else {
    return 'Drag and drop your file here';
  }

};

Dropzone.propTypes = {
  onDrop: PropTypes.func,
  uploadingIndicator: PropTypes.element,
};

export { getColor, getMessage };

export default Dropzone;
